import styled from "@emotion/styled/macro";
import {
  Button,
  Checkbox,
  LoadingOverlay,
  Typography,
  LoadingIndicator,
  tokens,
} from "@sunrun/experience-ui-components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import type {
  Contact,
  Role,
} from "@sunrun/experience-ui-components/lib/components/ContactCard";
import { useMediaQuery } from "react-responsive";
import { CardContent, CardHeader } from "../../molecules/Card";
import { CheckoutContacts } from "../CheckoutContacts";
import { Mixpanel } from "providers/mixpanel";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useCheckoutFooter } from "checkout/providers/CheckoutFooterContext";
import { Card, CardHeaderDivider } from "components/atoms/Card";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { ErrorModal } from "components/molecules/ErrorModal";
import {
  ContactsUnavailableModal,
  isProspectNotQualifiedContactsError,
} from "components/organisms/ContactsUnavailableModal";
import { ProspectUnavailableModal } from "components/organisms/ProspectUnavailableModal";
import { isProspectUnavailable } from "utils/contactsErrors";
import { confirmContacts } from "services/contacts";
import { findContact, getPrimaryContact } from "utils/contacts";
import { isEmptyValue, ROLES } from "utils/usageUtils";
import { useContactManagement } from "hooks/useContactManagement";
import { usePrevious } from "hooks/usePrevious";
import { useUsageData } from "utils/swrHooks";
import { useAuthToken } from "hooks/useAuthToken";
import { HorizontalLine } from "components/atoms/HorizontalLine";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";
import { CUSTOMER_SELF_CHECKOUT__CLICKED_CONTINUE_CTA_CONFIRM_NAMES_TAB_CHECKOUT_PAGE } from "constants/mixpanelEvents";

const ConfirmNamesV1: React.FC = () => {
  const authKey = useAuthToken("HYBRID")?.token ?? "";
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const isCustomer = !!proposalToken;
  const { prospectId, offerId } = useParams();
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const {
    proposalId,
    navigateToNextTask,
    currentTask,
    refreshAndNavigateNext,
    isNewHomes,
    userProfile,
  } = useCheckoutContext();
  const {
    prospect,
    error: prospectError,
    isLoading: isProspectLoading,
  } = useUsageData(prospectId);

  const {
    contactList: contacts,
    isContactsLoading,
    contactsError,
    onEditContact,
    onEditContactRole,
    refetch,
    error: contactError,
    setError: setContactError,
    loading: updateContactLoading,
  } = useContactManagement();

  const selectedPrimaryContact = getPrimaryContact(contacts);
  const selectedUtilityContact = findContact(
    contacts,
    ROLES.UTILITYBILLCONTACT
  );
  const selectedSecondaryContact = findContact(
    contacts,
    ROLES.CONTRACTCOSIGNER
  );

  const previousPrimaryContact = usePrevious(selectedPrimaryContact);
  const [originalPrimaryContact, setOriginalPrimaryContact] =
    useState<Contact>();
  const previousUtilityContact = usePrevious(selectedUtilityContact);
  const [originalUtilityContact, setOriginalUtilityContact] =
    useState<Contact>();
  const previousSecondaryContact = usePrevious(selectedSecondaryContact);
  const [originalSecondaryContact, setOriginalSecondaryContact] =
    useState<Contact>();

  useEffect(() => {
    if (!previousPrimaryContact && selectedPrimaryContact) {
      setOriginalPrimaryContact(selectedPrimaryContact);
    }
  }, [previousPrimaryContact, selectedPrimaryContact]);

  useEffect(() => {
    if (!previousUtilityContact && selectedUtilityContact) {
      setOriginalUtilityContact(selectedUtilityContact);
    }
  }, [previousUtilityContact, selectedUtilityContact]);

  useEffect(() => {
    if (!previousSecondaryContact && selectedSecondaryContact) {
      setOriginalSecondaryContact(selectedSecondaryContact);
    }
  }, [previousSecondaryContact, selectedSecondaryContact]);

  const [error, setError] = useState<{ action?: string; error?: Error }>();
  const [isSaving, setIsSaving] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const contactsHaveInvalidContactInfo = () => {
    const requiredContactsHaveValidContactInfo =
      isEmptyValue(selectedPrimaryContact?.customerEmail) ||
      isEmptyValue(selectedPrimaryContact?.customerPrimaryPhone) ||
      isEmptyValue(selectedUtilityContact?.customerEmail) ||
      isEmptyValue(selectedUtilityContact?.customerPrimaryPhone);
    const secondaryInvalid =
      selectedSecondaryContact?.contactId &&
      (isEmptyValue(selectedSecondaryContact?.customerEmail) ||
        isEmptyValue(selectedSecondaryContact?.customerPrimaryPhone));

    return requiredContactsHaveValidContactInfo || secondaryInvalid;
  };

  const contactsHaveChanged = useMemo(() => {
    const requiredContactsHaveChanged =
      originalPrimaryContact?.contactId !== selectedPrimaryContact?.contactId ||
      originalUtilityContact?.contactId !== selectedUtilityContact?.contactId;

    const secondaryChanged =
      originalSecondaryContact?.contactId !==
      selectedSecondaryContact?.contactId;

    return requiredContactsHaveChanged || secondaryChanged;
  }, [
    originalPrimaryContact?.contactId,
    originalSecondaryContact?.contactId,
    originalUtilityContact?.contactId,
    selectedPrimaryContact?.contactId,
    selectedSecondaryContact?.contactId,
    selectedUtilityContact?.contactId,
  ]);

  const handleConfirm = () => {
    if (!selectedPrimaryContact || !selectedUtilityContact) {
      setError({
        action: "Confirm Names Task View",
        error: new Error(
          "You must select a Primary signer and a Utility contact."
        ),
      });
      return;
    }
    if (contactsHaveInvalidContactInfo()) {
      setError({
        action: "Confirm Names Task View",
        error: new Error(
          "All selected contacts must both have an email and phone number."
        ),
      });
      return;
    }

    if (!prospectId || !offerId) {
      throw new Error("prospectId or offerId are undefined");
    }

    // If contacts haven't changed, AND ODI isComplete THEN skip confirmContacts
    if (!contactsHaveChanged && currentTask?.isComplete) {
      navigateToNextTask();
      return;
    }

    const contact1 = {
      contactId: selectedPrimaryContact?.contactId,
      role: ROLES.HOMEOWNER,
    };
    const contact2 = {
      contactId: selectedUtilityContact?.contactId,
      role: ROLES.UTILITYBILLCONTACT,
    };

    let contactsArr = [contact1, contact2];
    if (selectedSecondaryContact?.contactId) {
      if (
        isEmptyValue(selectedSecondaryContact?.customerEmail) ||
        isEmptyValue(selectedSecondaryContact?.customerPrimaryPhone)
      ) {
        setError({
          action: "Confirm Names Task View",
          error: new Error("You must select a valid secondary signer."),
        });
        return;
      }

      contactsArr.push({
        contactId: selectedSecondaryContact?.contactId,
        role: ROLES.CONTRACTCOSIGNER,
      });
    }

    setIsSaving(true);

    confirmContacts(
      authKey || proposalToken || "",
      prospectId,
      proposalId,
      contactsArr,
      !!proposalToken
    )
      .then(() => {
        refetch();
        refreshAndNavigateNext();
      })
      .catch((err) => {
        setError({ action: "Confirm Names Task View", error: err });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleContactUpdate = useCallback(
    async (newContact: Contact, role: Role, currentContact?: Contact) => {
      if (newContact?.contactId !== currentContact?.contactId) {
        setIsNew(!newContact?.contactId);
        // Update ROLE of the contact;
        await onEditContact({
          ...newContact,
          role,
          // Set contact as primary if changing role to Homeowner
          ...(role === ROLES.HOMEOWNER && { primary: true }),
        });
        setConfirmed(false);
      }
    },
    [onEditContact]
  );

  const handleContactRoleUpdate = useCallback(
    async (newContact: Contact, roleToUpdate: Role, newRole: Role) => {
      try {
        // Update ROLE of the contact
        await onEditContactRole({ ...newContact, roleToUpdate, newRole });
        setConfirmed(false);
      } catch (error) {
        setError({ action: "Updating Contact", error: error as Error });
      }
    },
    [onEditContactRole]
  );

  const showCheckbox = !currentTask?.isComplete || contactsHaveChanged;
  const checkboxDisabled = Boolean(
    selectedPrimaryContact === undefined ||
      selectedUtilityContact === undefined ||
      contactsHaveInvalidContactInfo()
  );
  const confirmButtonDisabled =
    checkboxDisabled || (showCheckbox && !confirmed);

  const showFooter =
    !prospectError &&
    !contactsError &&
    !isProspectLoading &&
    !isContactsLoading &&
    !isSaving;

  useCheckoutFooter(
    showFooter ? (
      <StyledFooter isMobile={isMobile}>
        <StyledFooterButton
          isMobile={isMobile}
          disabled={confirmButtonDisabled}
          onClick={() => {
            if (isCustomer) {
              Mixpanel.track(
                CUSTOMER_SELF_CHECKOUT__CLICKED_CONTINUE_CTA_CONFIRM_NAMES_TAB_CHECKOUT_PAGE,
                {
                  prospectId,
                  proposalId,
                }
              );
            }
            handleConfirm();
          }}
        >
          Confirm
        </StyledFooterButton>

        {showCheckbox && (
          <StyledCheckbox
            isMobile={isMobile}
            checked={confirmed}
            disabled={checkboxDisabled}
            label="I verify that the above information is correct"
            onChange={() => {
              setConfirmed((confirmed) => !confirmed);
            }}
          />
        )}
        {isMobile && <StyledHorizontalLine />}
      </StyledFooter>
    ) : null,
    [
      showFooter,
      isMobile,
      confirmButtonDisabled,
      showCheckbox,
      checkboxDisabled,
      confirmed,
      isCustomer,
      prospectId,
      proposalId,
    ]
  );

  if (
    !isProspectLoading &&
    !prospectError &&
    isProspectUnavailable({
      prospect,
      prospectLoading: isProspectLoading,
    })
  ) {
    return <ProspectUnavailableModal />;
  }

  if (prospectError) {
    return (
      <ErrorComponent
        context="ConfirmNamesTaskView"
        action="Get Prospect"
        error={prospectError}
      />
    );
  }

  if (contactsError && isProspectNotQualifiedContactsError(contactsError)) {
    return (
      <ContactsUnavailableModal
        prospect={prospect}
        userProfile={userProfile}
        errorMessage={contactsError?.response?.body?.message}
      />
    );
  }

  if (contactsError) {
    return (
      <ErrorComponent
        context="ConfirmNamesTaskView"
        action="Get Contacts"
        error={contactsError}
      />
    );
  }

  if (isProspectLoading || isContactsLoading || isSaving) {
    return <LoadingIndicator fullScreen />;
  }

  return (
    <>
      {error && (
        <ErrorModal
          context="ConfirmNamesTaskView"
          error={error.error}
          action={error.action}
          onClose={() => setError(undefined)}
        />
      )}
      {contactError && (
        <ErrorModal
          title={contactError.title}
          context="ConfirmNamesTaskView"
          error={contactError.error}
          action={contactError.action}
          onClose={() => setContactError(undefined)}
          onRetry={contactError.retry}
        />
      )}
      <GridLayoutDiv isMobile={isMobile}>
        <Typography variant="p">
          Please confirm that the primary signer's name matches the name of a
          person on the title. If the primary signer and name on title DO NOT
          match, change the primary signer.
        </Typography>
        {!isNewHomes && (
          <Card isMobile={isMobile}>
            <CardHeader isMobile={isMobile}>
              <Typography
                size={isMobile ? tokens.FONT_SIZE_3 : tokens.FONT_SIZE_1}
                fontWeight={isMobile ? "medium" : "regular"}
              >
                Names on Title
              </Typography>
            </CardHeader>
            <CardHeaderDivider isMobile={isMobile} />
            <CardContent isMobile={isMobile}>
              <Typography
                variant="p"
                size={isMobile ? tokens.FONT_SIZE_6 : tokens.FONT_SIZE_1}
              >
                {prospect?.primaryTitle ?? ""}
                {prospect?.primaryTitle && prospect.secondaryTitle && ", "}
                {prospect?.secondaryTitle ?? ""}
                {!prospect?.primaryTitle && !prospect?.secondaryTitle && (
                  <StyledNoNameOnTitleText>
                    Name on title data not available, please confirm customer is
                    on title.
                  </StyledNoNameOnTitleText>
                )}
              </Typography>
            </CardContent>
          </Card>
        )}
        <CheckoutContacts
          hideUtilityContactCard={isNewHomes}
          selectedPrimaryContact={selectedPrimaryContact}
          setSelectedPrimaryContact={async (contact) =>
            await handleContactUpdate(
              contact,
              ROLES.HOMEOWNER,
              selectedPrimaryContact
            )
          }
          selectedUtilityContact={selectedUtilityContact}
          setSelectedUtilityContact={async (contact) =>
            await handleContactUpdate(
              contact,
              ROLES.UTILITYBILLCONTACT,
              selectedUtilityContact
            )
          }
          selectedSecondaryContact={selectedSecondaryContact}
          setSelectedSecondaryContact={async (contact, remove) => {
            remove
              ? // If remove = true, selectedSecondaryContact is defined
                await handleContactRoleUpdate(
                  selectedSecondaryContact!,
                  ROLES.CONTRACTCOSIGNER,
                  ROLES.OTHER
                )
              : await handleContactUpdate(
                  contact,
                  ROLES.CONTRACTCOSIGNER,
                  selectedSecondaryContact
                );
          }}
        />
      </GridLayoutDiv>
      {updateContactLoading && (
        <LoadingOverlay
          message={`${isNew ? "Creating" : "Updating"} contact...`}
        />
      )}
    </>
  );
};

const StyledCheckbox = styled(Checkbox)<{ isMobile?: boolean }>`
  span {
    padding-left: 5px;
    font-size: 16px;
  }
  margin-bottom: ${(props) => (props.isMobile ? "16px" : "0px")};
`;

const StyledHorizontalLine = styled(HorizontalLine)`
  width: 100%;
  margin: auto 0;
  border-color: ${tokens.BORDER_LIGHT};
  margin-bottom: 16px;
`;

const StyledFooter = styled.footer<{ isMobile?: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: ${(props) =>
    props.isMobile ? "column-reverse" : "row-reverse"};
`;

const GridLayoutDiv = styled.div<{ isMobile?: boolean }>`
  display: ${(props) => (props.isMobile ? "flex" : "grid")};
  margin-top: ${(props) => (props.isMobile ? "0px" : "32px")};
  grid-row-gap: ${(props) => (props.isMobile ? "16px" : "32px")};
  margin-bottom: ${(props) => (props.isMobile ? "16px" : "32px")};
  ${({ isMobile }) =>
    isMobile &&
    `
    flex-direction: column;
  `}
`;

const StyledNoNameOnTitleText = styled.p`
  font-size: ${tokens.FONT_SIZE_1}px;
  margin: 0;
  line-height: normal;
`;

const StyledFooterButton = styled(Button)<{ isMobile?: boolean }>`
  width: ${(props) => (props.isMobile ? "100%" : "120px")};
`;

export { ConfirmNamesV1 };
